/**
 * <color-family-selector> — metaobject-driven colour picker for the product page.
 *
 * Server-rendered (good for SEO/perf). Two interactions, both morph-safe because
 * the element self-initialises on connectedCallback:
 *   1. Family buttons (role=radio) filter which "Select colour" row is shown — no nav.
 *   2. Colour swatches are plain <a> links → the browser navigates to that colour's
 *      product. The destination page re-detects the current product in Liquid, so the
 *      right family + colour load pre-selected.
 *
 * See snippets/product-color-family.liquid for the markup.
 */
if (!customElements.get('color-family-selector')) {
  class ColorFamilySelector extends HTMLElement {
    connectedCallback() {
      this.familyButtons = Array.from(this.querySelectorAll('[data-family-index]'));
      this.colourRows = Array.from(this.querySelectorAll('[data-colour-row]'));
      this.familyName = this.querySelector('[data-family-name]');
      this.colourName = this.querySelector('[data-colour-name]');
      this.grid = this.querySelector('.color-family__grid');
      this.prevBtn = this.querySelector('[data-family-prev]');
      this.nextBtn = this.querySelector('[data-family-next]');

      if (this.familyButtons.length === 0) return;

      this.currentFamily = parseInt(this.dataset.currentFamily, 10) || 0;
      this.currentColourLabel = this.colourName ? this.colourName.textContent.trim() : '';

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.updateArrows = this.updateArrows.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      if (this.grid) {
        this.grid.addEventListener('scroll', this.updateArrows, { passive: true });
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1));
        this.updateArrows();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.grid) this.grid.removeEventListener('scroll', this.updateArrows);
    }

    onClick(event) {
      const button = event.target.closest('[data-family-index]');
      if (button && this.contains(button)) {
        const index = parseInt(button.dataset.familyIndex, 10);
        this.selectFamily(index);
      }
      // Colour links are left to navigate natively (no preventDefault).
    }

    onKeydown(event) {
      const button = event.target.closest('[data-family-index]');
      if (!button) return;
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (!(event.key in keys)) return;
      event.preventDefault();
      const next = Math.min(
        Math.max(parseInt(button.dataset.familyIndex, 10) + keys[event.key], 0),
        this.familyButtons.length - 1
      );
      this.selectFamily(next, true);
    }

    selectFamily(index, focus = false) {
      this.familyButtons.forEach((button, i) => {
        const active = i === index;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-checked', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
        if (active && focus) button.focus();
      });

      this.colourRows.forEach((row) => {
        row.toggleAttribute('hidden', parseInt(row.dataset.colourRow, 10) !== index);
      });

      const selected = this.familyButtons[index];
      if (this.familyName && selected) this.familyName.textContent = selected.dataset.familyLabel || '';

      // Show the current product's colour name only on its own family; blank elsewhere.
      if (this.colourName) {
        this.colourName.textContent = index === this.currentFamily ? this.currentColourLabel : '';
      }
    }

    page(direction) {
      if (!this.grid) return;
      this.grid.scrollBy({ left: direction * this.grid.clientWidth, behavior: 'smooth' });
    }

    updateArrows() {
      if (!this.grid || !this.prevBtn || !this.nextBtn) return;
      const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
      const atStart = this.grid.scrollLeft <= 1;
      const atEnd = this.grid.scrollLeft >= maxScroll - 1;
      const noOverflow = maxScroll <= 1;
      this.prevBtn.setAttribute('aria-disabled', atStart || noOverflow ? 'true' : 'false');
      this.nextBtn.setAttribute('aria-disabled', atEnd || noOverflow ? 'true' : 'false');
    }
  }

  customElements.define('color-family-selector', ColorFamilySelector);
}
